import React from 'react';
import { Card, Button, Typography, Form, Input, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateControl } from '@api/controls';
import { useSetAtom } from 'jotai';
import { settingsModalVisibleAtom } from '../NexaStore';
import { NEXA_LEGAL_ACCEPTANCE_KEY } from '@src/utils/constants/controls';
import { logger } from '@src/utils/misc/logger';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

export const DisableNexa: React.FC = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const setSettingsModalVisible = useSetAtom(settingsModalVisibleAtom);

  const disableNexaMutation = useMutation({
    mutationFn: (reason: string) =>
      updateControl(NEXA_LEGAL_ACCEPTANCE_KEY, {
        status: false,
        value: null,
        reason: reason || 'Admin disabled Nexa access',
      }),
    onSuccess: () => {
      // Invalidate and refetch controls init query
      queryClient.invalidateQueries(['controls-init']);
      message.success('Nexa has been disabled successfully!');
      form.resetFields();
      // Close the settings modal to show the updated Nexa state
      setTimeout(() => {
        setSettingsModalVisible(false);
      }, 1500); // Give time for user to see the success message
    },
    onError: (error: any) => {
      logger.log('Failed to disable Nexa:', error);
      message.error('Failed to disable Nexa. Please try again.');
    },
  });

  const handleDisable = () => {
    form.validateFields().then((values) => {
      disableNexaMutation.mutate(values.reason);
    });
  };

  return (
    <Card>
      <Title level={4}>Sorry to see you go...</Title>

      <Paragraph>
        Tell us why you&apos;re disabling Nexa - It&apos;ll help us make it better for you.
      </Paragraph>

      <Form form={form} layout="vertical">
        <Form.Item name="reason" label="Reason:">
          <TextArea
            rows={3}
            placeholder="e.g., Results were inaccurate, Performance issues, etc."
            maxLength={255}
          />
        </Form.Item>
      </Form>

      <Paragraph>
        <Text type="secondary">
          Note: Disabling Nexa will immediately remove access for all users in your organization.
          You can re-enable Nexa access at any time from the main Nexa interface.
        </Text>
      </Paragraph>

      <Button
        type="primary"
        danger
        loading={disableNexaMutation.isLoading}
        onClick={handleDisable}
        style={{ marginTop: 16, float: 'right' }}
      >
        {disableNexaMutation.isLoading ? 'Disabling...' : 'Disable Nexa'}
      </Button>
    </Card>
  );
};
